import { TranslateService } from '@ngx-translate/core';
import { CheckedDataService, ICheckedEntity } from '@se/se.platform.base';
import { HardcodedData, JsonObject } from '@se/cp.common';
import { DataBinding } from '@se/se.platform.base';
import { ExportSegmentalKineticParams, IExportSegment, IExportSegmentValue } from './interfaces';
import { groupBy } from 'lodash-es';

const NEW_LINE_CHAR = '<BR>';
export class ExportSegmentalKinetic {
    constructor(
        private dataService: CheckedDataService,
        private dataSourceService: CheckedDataService,
        private translateService: TranslateService,
    ) {}

    private parseExportSegment = (segment: ICheckedEntity, kineticEntries?: ICheckedEntity): IExportSegment => {
        const regionID = segment.getPropertyPrimitiveValue('RegionID');
        const name = typeof regionID === 'string' ? regionID : '';
        const descriptionForPrint =
            segment.getPropertyPrimitiveValue('DescriptionForPrint') ??
            segment.getPropertyPrimitiveValue('Description');
        const description = typeof descriptionForPrint === 'string' ? descriptionForPrint : '';
        return {
            name,
            description,
            value: this.parseExportSegmentValue(segment, kineticEntries),
        };
    };

    private parseExportSegmentValue = (
        segment: ICheckedEntity,
        kineticEntries?: ICheckedEntity,
    ): IExportSegmentValue => {
        const regionID = segment.getPropertyPrimitiveValue('RegionID');
        const name = typeof regionID === 'string' ? regionID : '';
        const entityValue = kineticEntries?.getPropertyPrimitiveValue(name);
        if (!entityValue)
            return {
                code: '0',
                description: '',
            };
        const descriptionForPrint =
            (entityValue as JsonObject)?.['DescriptionForPrint'] ?? (entityValue as JsonObject)?.['Description'];
        const description = typeof descriptionForPrint === 'string' ? descriptionForPrint : '';
        const _code = (entityValue as JsonObject)?.['Code'];
        const code = typeof _code === 'string' ? _code : '';
        return {
            code,
            description,
        };
    };

    private getGroupText = (code: string, codeGroups: string[]): string => {
        let text: string = '';
        switch (code) {
            case '0':
                text = codeGroups[0] ?? '';
                break;
            case '1':
                text = codeGroups[1] ?? '';
                break;
            case '2':
                text = codeGroups[2] ?? '';
                break;
            case '4':
                text = codeGroups[3] ?? '';
                break;
            case 'X':
                text = codeGroups[4] ?? '';
                break;
        }
        return text;
    };

    private parseHTMLSegments = (
        segments: IExportSegment[],
        title: string,
        boldTitle: boolean,
        segmentLabelSeparator: string,
        segmentSeparator: string,
    ): string => {
        const kineticTitle = boldTitle ? `<strong>${title}</strong>` : title;
        const labelNewLine = segmentLabelSeparator.includes(NEW_LINE_CHAR);
        const labelSeparator = segmentLabelSeparator.replaceAll(NEW_LINE_CHAR, '');
        const segmentNewLine = segmentSeparator.includes(NEW_LINE_CHAR);
        const rowSeparator = segmentSeparator.replaceAll(NEW_LINE_CHAR, '');
        const textRows = segments.map((segment) => {
            let row = `${segment.description}${labelSeparator}`;
            if (labelNewLine) row += '<p>';
            row += `${segment.value?.description}${rowSeparator}`;
            if (labelNewLine) row += '</p>';
            return row;
        });
        const text = segmentNewLine ? textRows.map((row) => `<p>${row}</p>`).join('') : textRows.join('');
        return `<p>${kineticTitle}</p>${text}`;
    };

    private parseGroupedHTMLSegments = (
        segments: IExportSegment[],
        title: string,
        boldTitle: boolean,
        segmentLabelSeparator: string,
        segmentValueSeparator: string,
        segmentSeparator: string,
    ): string => {
        const kineticTitle = boldTitle ? `<strong>${title}</strong>` : title;
        const labelNewLine = segmentLabelSeparator.includes(NEW_LINE_CHAR);
        const labelSeparator = segmentLabelSeparator.replaceAll(NEW_LINE_CHAR, '');
        const valueNewLine = segmentValueSeparator.includes(NEW_LINE_CHAR);
        const valueSeparator = segmentValueSeparator.replaceAll(NEW_LINE_CHAR, '');
        const segmentNewLine = segmentSeparator.includes(NEW_LINE_CHAR);
        const rowSeparator = segmentSeparator.replaceAll(NEW_LINE_CHAR, '');
        const grouped = groupBy(segments, (segment) => segment.value?.description);
        let text = '';
        Object.entries(grouped).forEach(([label, item]) => {
            if (segmentNewLine) text += '<p>';
            text += `${label}${labelSeparator}`;
            if (labelNewLine) text += '<p>';
            item.forEach((segment, index) => {
                if (valueNewLine) text += '<p>';
                text += `${segment.description}`;
                if (index < item.length - 1) text += `${valueSeparator}`;
                if (valueNewLine) text += '</p>';
            });
            if (labelNewLine) text += '</p>';
            text += `${rowSeparator}`;
            if (segmentNewLine) text += '</p>';
        });
        return `<p>${kineticTitle}</p>${text}`;
    };

    private parseHTMLSameValues = (code: string, title: string, boldTitle: boolean, codeGroups: string[]): string => {
        const text = this.getGroupText(code, codeGroups);
        const kineticTitle = boldTitle ? `<strong>${title}</strong>` : title;
        return `<p>${kineticTitle}</p><p>${text}</p>`;
    };

    private parseTextSegments = (
        segments: IExportSegment[],
        title: string,
        segmentLabelSeparator: string,
        segmentSeparator: string,
    ): string => {
        const textRows = segments.map((segment) => {
            `${segment.description}${segmentLabelSeparator}${segment.value?.description}`;
        });
        const text = textRows.join(segmentSeparator);
        return `${title}\n${text}`;
    };

    private parseGroupedTextSegments = (
        segments: IExportSegment[],
        title: string,
        segmentLabelSeparator: string,
        segmentValueSeparator: string,
        segmentSeparator: string,
    ): string => {
        const grouped = groupBy(segments, (segment) => segment.value?.description);
        let text: string = '';
        Object.entries(grouped).forEach(([label, item]) => {
            text += `${label}${segmentLabelSeparator}`;
            item.forEach((segment, index) => {
                text += `${segment.description}`;
                if (index < item.length - 1) text += `${segmentValueSeparator}`;
            });
            text += `${segmentSeparator}`;
        });
        return `${title}\n${text}`;
    };

    private parseTextSameValues = (code: string, title: string, codeGroups: string[]): string => {
        const text = this.getGroupText(code, codeGroups);
        return `${title}\n${text}`;
    };

    private getSameValues = (exportSegments: IExportSegment[]): string | null => {
        if (exportSegments.length === 0) return '0';
        const valueToCompare = exportSegments[0].value?.code ?? '0';
        if (exportSegments.every((segment) => segment.value?.code === valueToCompare)) {
            return valueToCompare;
        }
        return null;
    };

    private filterSegmentToDisplay = (exportSegments: IExportSegment[]): IExportSegment[] =>
        exportSegments.filter((segment) => ['2', '3', '4', '5'].includes(segment.value?.code ?? ''));

    private checkTargetIsEmpty = (target: string): boolean => {
        const targetValue = this.getTargetValue(target);
        if (!targetValue) return true;
        const cleared = targetValue
            .replace(/(<([^>]+)>)/gi, '')
            .replace(/\n/g, '')
            .trim();
        return cleared.length === 0;
    };

    private getTargetValue = (target: string): string => {
        if (!this.dataService?.getValue || !target) return '';
        const res = this.dataService.getValue(new DataBinding(target));
        if (typeof res !== 'string') return '';
        return res;
    };

    export(params: ExportSegmentalKineticParams) {
        const [
            isHtml,
            groupedByValue,
            title,
            boldTitle,
            segmentLabelSeparator,
            segmentValueSeparator,
            segmentSeparator,
            target,
            ...codeGroups
        ] = params;
        const kineticEntries = this.dataService.getSelectedEntity(HardcodedData.bindings.ecoCv.lvKinetic);
        const segments = this.dataSourceService.getEntities(
            new DataBinding(HardcodedData.lists.leftVentricleSegmentList),
        );
        const exportSegments: IExportSegment[] =
            segments
                ?.map((segment) => this.parseExportSegment(segment, kineticEntries))
                .filter((segment) => segment.value !== null) ?? [];
        const sameValues = this.getSameValues(exportSegments);
        let textToExport: string = '';
        if (sameValues && !['3', '5'].includes(sameValues)) {
            textToExport = isHtml
                ? this.parseHTMLSameValues(sameValues, title, boldTitle, codeGroups)
                : this.parseTextSameValues(sameValues, title, codeGroups);
        } else if (isHtml && !groupedByValue) {
            textToExport = this.parseHTMLSegments(
                this.filterSegmentToDisplay(exportSegments),
                title,
                boldTitle,
                segmentLabelSeparator,
                segmentSeparator,
            );
        } else if (isHtml && groupedByValue) {
            textToExport = this.parseGroupedHTMLSegments(
                this.filterSegmentToDisplay(exportSegments),
                title,
                boldTitle,
                segmentLabelSeparator,
                segmentValueSeparator,
                segmentSeparator,
            );
        } else if (!isHtml && groupedByValue) {
            textToExport = this.parseGroupedTextSegments(
                this.filterSegmentToDisplay(exportSegments),
                title,
                segmentLabelSeparator,
                segmentValueSeparator,
                segmentSeparator,
            );
        } else if (!isHtml && !groupedByValue) {
            textToExport = this.parseTextSegments(
                this.filterSegmentToDisplay(exportSegments),
                title,
                segmentLabelSeparator,
                segmentSeparator,
            );
        }

        if (!this.checkTargetIsEmpty(target)) {
            const confirmText = this.translateService.instant('ExportClinicalEvalAskToAppend');
            const res = window.confirm(confirmText); //ok to appena cancel to overwrite
            if (res) {
                const targetValue = this.getTargetValue(target);
                return `${targetValue}${textToExport}`;
            }
        }
        return textToExport;
    }
}
